import React, { useState } from "react";
import { useRouter } from "next/router";
import { useSelector } from "react-redux";

import {
  BrandCatalogCategories,
  BrandCatalogData,
  BrandCatalogProduct,
  getProductCatalog,
} from "api/requests/getProductCatalog";
import { Brand } from "custom-types/Brand";
import logger from "lib/logger";
import { getDomainCountryCode } from "redux/selectors/config";
import { getCoordinates } from "redux/selectors/location";

import PageNavigation from "components/PageNavigation";

import { CardGrid } from "./CardGrid";
import ProductCard from "./ProductCard";
import ProductCardPlaceholder from "./ProductCardPlaceholder";
import RadioChipTray from "./RadioChipTray";

type Props = {
  brand: Brand;
  className?: string;
  onProductsFetch?: (args: { products: BrandCatalogProduct[] }) => void;
  initialCategory?: string;
};

const ProductCatalogSection = ({
  brand,
  className,
  onProductsFetch = () => {},
  initialCategory,
}: Props) => {
  const router = useRouter();
  const coordinates = useSelector(getCoordinates);
  const countryCode = useSelector(getDomainCountryCode);
  const productsPerPage = 24;

  const [products, setProducts] = React.useState<BrandCatalogProduct[]>([]);
  const [categories, setCategories] = React.useState<
    { label: string; value: string }[]
  >([]);
  const [currentCategorySlug, setCurrentCategorySlug] = React.useState(
    initialCategory || ""
  );
  const [totalProducts, setTotalProducts] = React.useState(0);

  const [isLoading, setIsLoading] = useState(true);

  const getPage = () => {
    // always return an integer >= 1
    let page = router.query?.catalogPage || 1;
    if (Array.isArray(page)) {
      page = page[0];
    }
    // cast strings (and floats) into integer or NaN:
    page = parseInt(page.toString(), 10);
    if (isNaN(page) || page < 1) {
      page = 1;
    }
    return page;
  };

  const pushPage = ({
    page,
    productCategory,
  }: {
    page: number;
    productCategory?: string;
  }) => {
    router.push(
      {
        query: {
          ...router.query,
          catalogPage: page,
          productCategory,
        },
      },
      undefined,
      { scroll: false, shallow: true }
    );
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
    const catalog = document.getElementById("product-catalog") as any;
    catalog &&
      catalog.scrollIntoView({ behavior: "smooth" }) &&
      catalog.focus();
  };

  const changeCategory = (categorySlug: string) => {
    // When changing category, go back to page 1.
    if (currentCategorySlug !== categorySlug) {
      pushPage({ page: 1, productCategory: categorySlug });
      setCurrentCategorySlug(categorySlug);
    }
  };

  const resetPage = () => {
    router.replace(
      {
        query: {
          ...router.query,
          catalogPage: 1,
          productCategory: currentCategorySlug,
        },
      },
      undefined,
      { scroll: false, shallow: true }
    );
  };

  const currentCategoryCount = (
    totalProductCount: number,
    categoryData: BrandCatalogCategories
  ): number => {
    if (currentCategorySlug) {
      const category = categoryData.filter(
        (c) => c.categorySlug === currentCategorySlug
      );
      return category?.length ? category[0].productCount : 0;
    } else {
      return totalProductCount;
    }
  };

  React.useEffect(() => {
    // getPage corrects NaN's and values <1 to 1.
    const page = getPage();

    getProductCatalog({
      brandSlug: brand.slug,
      categorySlug: currentCategorySlug,
      coordinates: coordinates,
      countryCode,
      skip: (page - 1) * productsPerPage,
      take: productsPerPage,
    })
      .then((data: BrandCatalogData) => {
        const totalProducts = currentCategoryCount(
          data.metadata.totalCount,
          data.metadata.categories
        );

        // is the page param too large?  Don't just show nothing...
        if ((page - 1) * productsPerPage > totalProducts) {
          resetPage();
          return;
        }

        const categories = [
          { label: "All results", value: "" },
          ...data.metadata.categories.map((cat) => ({
            label: cat.categoryName,
            value: cat.categorySlug,
          })),
        ];
        setCategories(categories);
        setTotalProducts(totalProducts);
        setProducts(data.data);

        setIsLoading(false);

        onProductsFetch({ products: data.data });
      })
      .catch((err) => {
        logger.error(err);
        setIsLoading(false);
      });
  }, [currentCategorySlug, brand.slug, router.query?.catalogPage]);

  return (
    <section className={className} data-testid="product-catalog-section">
      <h2 className="mb-sm">Products by {brand.name}</h2>
      {!isLoading && !products?.length ? (
        <div className="py-lg" />
      ) : (
        <RadioChipTray
          isLoading={isLoading}
          chips={categories}
          legend="Show me"
          onSelectionChange={changeCategory}
          currentCategorySlug={currentCategorySlug}
        />
      )}
      {!isLoading && !products?.length ? (
        <div
          style={{ height: 200 }}
          className="flex items-center justify-center"
        >
          <p>{brand.name} does not have any products listed on Leafly yet.</p>
        </div>
      ) : (
        <>
          <CardGrid className="mt-lg">
            {isLoading
              ? [...Array(8)].map((_e, i) => (
                  <ProductCardPlaceholder
                    className="elevation-low rounded overflow-hidden p-lg pt-xl flex flex-col justify-between w-full"
                    key={`product-card-placeholder-${i}`}
                  />
                ))
              : products.map((product) => (
                  <ProductCard product={product} key={product.id} />
                ))}
          </CardGrid>

          <PageNavigation
            currentPage={getPage()}
            totalPages={Math.ceil(totalProducts / productsPerPage)}
            onPrev={() => {
              pushPage({
                page: getPage() - 1,
                productCategory: currentCategorySlug,
              });
            }}
            onNext={() => {
              pushPage({
                page: getPage() + 1,
                productCategory: currentCategorySlug,
              });
            }}
          />
        </>
      )}
    </section>
  );
};

export default ProductCatalogSection;
